<script setup lang="ts">
    import {
        getAllApplicationAttributes,
        ApplicationAttributeDto,
        saveApplicationAttribute,
        deleteApplicationAttribute
    } from '@/shared/helpers/api';
    import { computed, ref } from 'vue';
    import { requireIsAdministrator as vRequireIsAdministrator } from '@/shared/directives/requirePermission';
    import { useAccessPolicyStore } from '@/stores/accessPolicyStore';
    import { getLibraryLink } from '@/shared/helpers/routing';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import ClientApplicationSelect, { ClientApplication } from '@/shared/components/ClientApplicationSelect.vue';
    import ViewBaseAccessPolicy from '@/apps/config/AccessPolicy/ViewBaseAccessPolicy.vue';
    import AccessPolicyPicker from '@/apps/config/AccessPolicy/AccessPolicyPicker.vue';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import useBuefy from '@/shared/composables/useBuefy';

    const { showError, showInfo } = useShow();
    const buefy = useBuefy();
    const store = useAccessPolicyStore();

    const clientAttributes = ref<ApplicationAttributeDto[]>();
    const loading = ref(false);

    const createNew = ref(false);
    const clientApplication = ref<ClientApplication | null>(null);
    const scope = ref('');

    const systemNames = computed(() => store.policySystems.map(s => s.name));
    const roleNames = computed(() => store.systemRoles.map(r => r.name));

    const validForSave = computed(() =>
        clientApplication.value
        && store.selectedSystem
        && store.selectedRole
        && scope.value);

    function onOpen() {
        store.clearSelection();
        clientApplication.value = null;
        scope.value = '';
        createNew.value = true;
    }

    function onCancel() {
        createNew.value = false;
        store.clearSelection();
    }

    async function onClickSave() {
        try {
            await saveApplicationAttribute(http, clientApplication.value!.objectId, {
                id: null,
                email: null,
                facilityRole: store.selectedRole!.name,
                sourceSystem: store.selectedSystem!.name,
                scope: scope.value!.trim(),
                commonLibrary: true
            });
            showInfo('Access attribute saved');
        } catch (error) {
            showError(error);
        } finally {
            onCancel();
            await reloadApplications();
        }
    }

    async function loadApplications(): Promise<void> {
        clientAttributes.value = await getAllApplicationAttributes(http);
    }

    async function reloadApplications(): Promise<void> {
        loading.value = true;
        try {
            await loadApplications();
        } catch {
            showError('Unable to load application attributes');
        } finally {
            loading.value = false;
        }
    }

    async function deleteAttribute(a: ApplicationAttributeDto): Promise<void> {
        await buefy.dialog.confirm({
            title: 'Please confirm delete',
            message: `Are you sure you want to delete [${a.system}:${a.role}:${a.scope}] for application ${a.applicationName}?`,
            type: 'is-danger',
            hasIcon: true,
            onConfirm: async () => {
                try {
                    await deleteApplicationAttribute(http, a);
                } catch (error) {
                    showError(error);
                } finally {
                    await reloadApplications();
                }
            }
        });
    }

    async function init(): Promise<void> {
        loading.value = true;
        try {
            await Promise.all([
                store.loadSystems(),
                store.loadScopes(),
                loadApplications()
            ]);
        } catch {
            showError('Unable to load data');
        } finally {
            loading.value = false;
        }
    }

    init();
</script>

<template>
    <view-base-access-policy>
        <template
            #controls>
            <div class="level">
                <div class="level-left mt-2 mb-3">
                    <b-button
                        v-require-is-administrator
                        class="is-info"
                        @click="onOpen">
                        <span class="icon is-small">
                            <i class="mdi mdi-plus" />
                        </span>
                        <span>
                            Add application access
                        </span>
                    </b-button>
                </div>
            </div>
        </template>
        <template #applicationattributes>
            <p class="block">
                Access attributes given to users registered as clients in the
                <router-link :to="getLibraryLink('CommonLibraryClientApplications')">
                    <span>CommonLibraryClientApplications</span>
                </router-link>
                library.
            </p>
            <b-table
                :data="clientAttributes"
                :loading="loading"
                striped
                narrowed
                hoverable>
                <b-table-column
                    label="ObjecId"
                    field="objectId"
                    sortable
                    searchable>
                    <template #default="props">
                        {{ props.row.objectId }}
                    </template>
                </b-table-column>
                <b-table-column
                    label="Application"
                    field="applicationName"
                    sortable
                    searchable>
                    <template #default="props">
                        <a :href="props.row.applicationIri">
                            {{ props.row.applicationName }}
                        </a>
                    </template>
                </b-table-column>
                <b-table-column
                    label="System"
                    field="system"
                    sortable
                    searchable>
                    <template #default="props">
                        {{ props.row.system }}
                    </template>
                </b-table-column>
                <b-table-column
                    label="Role"
                    field="role"
                    sortable
                    searchable>
                    <template #default="props">
                        {{ props.row.role }}
                    </template>
                </b-table-column>
                <b-table-column
                    label="Scope"
                    field="scope"
                    sortable
                    searchable>
                    <template #default="props">
                        {{ props.row.scope }}
                    </template>
                </b-table-column>
                <b-table-column
                    label=""
                    field="id">
                    <template #default="props">
                        <b-button
                            type="is-primary"
                            icon-left="delete"
                            label="Delete"
                            @click="deleteAttribute(props.row)" />
                    </template>
                </b-table-column>
            </b-table>
        </template>
        <template
            #modal>
            <modal-base
                v-if="createNew"
                @onCancel="onCancel">
                <template #modal-header>
                    Add new access role for client application
                </template>
                <template
                    #modal-body>
                    <p class="block">
                        For applications to be selectable here 1) they must have used the CommonLibrary API (which adds them to our users database table)
                        and 2) be registered as a valid client with the same Entra ID ObjectId in
                        <router-link :to="getLibraryLink('CommonLibraryClientApplications')">
                            <span>
                                CommonLibraryClientApplications
                            </span>
                        </router-link>
                        .
                    </p>
                    <client-application-select
                        v-model="clientApplication" />
                    <b-field
                        label="System">
                        <access-policy-picker
                            :model-value="store.selectedSystem?.name ?? null"
                            :options="systemNames"
                            placeholder="Select system"
                            @update:model-value="store.setSelectedSystem" />
                    </b-field>
                    <b-field
                        label="Role">
                        <access-policy-picker
                            :model-value="store.selectedRole?.name ?? null"
                            :options="roleNames"
                            :disabled="roleNames.length === 0"
                            placeholder="Select a Role"
                            @update:model-value="store.setSelectedRole" />
                    </b-field>
                    <b-field
                        label="Scope">
                        <b-input v-model="scope" />
                    </b-field>
                </template>
                <template #modal-footer>
                    <b-field
                        grouped
                        position="right">
                        <b-button
                            class="mt-2"
                            @click="onCancel">
                            Cancel
                        </b-button>
                        <b-button
                            class="is-primary mt-2"
                            :disabled="!validForSave"
                            @click="onClickSave">
                            Add
                        </b-button>
                    </b-field>
                </template>
            </modal-base>
        </template>
    </view-base-access-policy>
</template>
